/** Drawing-scale presets + the PDF points-per-metre constant, shared by the
 *  Takeoff scale picker (now a global-header pill) and the calibration flow.
 *
 *  PDF_PTS_PER_METRE converts a measured pixel count to a drawing-scale
 *  denominator. Derivation: 1 PDF point = 1/72 inch (PDF spec), 1 inch =
 *  0.0254 m, so 1 m = 39.3700787 inches = 72 * 39.3700787 PDF points =
 *  2834.645 pts/m. This bakes in the assumption that the PDF was authored at
 *  72 DPI (the standard); a PDF exported at a different DPI would need its own
 *  constant. Pixels-per-unit (metre) at scale 1:n = PDF_PTS_PER_METRE / n. */
export const PDF_PTS_PER_METRE = 72 * 39.3700787;

export interface ScalePreset {
  /** Scale denominator — 250 for "1 : 250". */
  n: number;
  label: string;
}

export const SCALE_PRESETS: ScalePreset[] = [
  { label: '1 : 1',    n: 1 },
  { label: '1 : 2',    n: 2 },
  { label: '1 : 5',    n: 5 },
  { label: '1 : 10',   n: 10 },
  { label: '1 : 20',   n: 20 },
  { label: '1 : 25',   n: 25 },
  { label: '1 : 50',   n: 50 },
  { label: '1 : 100',  n: 100 },
  { label: '1 : 200',  n: 200 },
  { label: '1 : 250',  n: 250 },
  { label: '1 : 500',  n: 500 },
  { label: '1 : 1000', n: 1000 },
  { label: '1 : 2000', n: 2000 },
  { label: '1 : 5000', n: 5000 },
];

/** The "1:n" denominator a stored per-metre pixels_per_unit implies, rounded
 *  to a whole number for display. Null when there is no usable calibration. */
export function scaleDenomFromCalibration(pixelsPerUnit: number | null | undefined): number | null {
  if (pixelsPerUnit == null || !Number.isFinite(pixelsPerUnit) || pixelsPerUnit <= 0) return null;
  const denom = Math.round(PDF_PTS_PER_METRE / pixelsPerUnit);
  // An enlargement (a 2:1 detail) has no whole 1:n denominator; "1 : 0" is
  // worse than no label.
  return denom >= 1 ? denom : null;
}

/** The one label format every scale picker compares against, so a stored
 *  '1 : 50' lights the 1 : 50 preset row on web, macOS and iOS alike. */
export function scaleLabelFor(denom: number): string {
  return `1 : ${denom}`;
}
